import { extractApiErrorCode } from '@hub/lib/api-error/extract-api-error-code';
import { getSessionQueryKey, refreshSession, verifyEmail } from '@platform/api';
import type { QueryClient } from '@tanstack/react-query';
import type { VerifyEmailErrorCode } from './errorCodes';

/** What both confirm-email routes render from, never `useAuthUser()`. */
export type EmailConfirmationRedemption = 'confirmed' | 'failed';

/** Shared by both confirm-email routes. Never throws — a dead/expired token isn't a Sentry event. */
export async function redeemEmailConfirmation(token: string | undefined, queryClient: QueryClient): Promise<EmailConfirmationRedemption> {
  if (!token) {
    return 'failed';
  }

  try {
    await verifyEmail({ body: { token }, throwOnError: true });
  } catch (error) {
    extractApiErrorCode(error, {
      flow: 'verify_email',
      byCode: new Set<VerifyEmailErrorCode>(['invalid-confirmation', 'user-not-found']),
    });
    return 'failed';
  }

  // Best-effort session refresh, for a visitor who also holds one in this browser. `skipUnauthorized`
  // keeps an anonymous visitor's 401 from tripping the fetch layer's redirect to auth.
  try {
    await refreshSession({ throwOnError: true, meta: { skipUnauthorized: true } });
    await queryClient.invalidateQueries({ queryKey: getSessionQueryKey() });
  } catch (error) {
    extractApiErrorCode(error, { flow: 'verify_email_session_refresh' });
  }

  return 'confirmed';
}
